import React from 'react';
import { Link } from 'react-router-dom';
import { Footer } from '../components/Footer';

const AndroidPrivacyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Simple Back Button */}
      <div className="container mx-auto px-4 py-6">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-purple-600 hover:text-purple-700 transition-colors font-medium"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Home
        </Link>
      </div>

      <div className="flex-grow px-4 pb-8">
        <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg p-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Privacy Policy</h1>
          <p className="text-gray-600 mb-6">TinnitusTune Android Application</p>

          <div className="space-y-6 text-gray-700">
            {/* Introduction */}
            <section className="pb-6 border-b border-gray-200">
              <p className="mb-4">
                <strong>Effective Date:</strong> {new Date().toLocaleDateString('en-GB')}
              </p>
              <p className="mb-4">
                <strong>Data Controller:</strong><br />
                Soepel IT<br />
                Enschede, Netherlands<br />
                Email: tinnitustune@soepel.it
              </p>
              <p>
                This Privacy Policy explains how the TinnitusTune Android application ("App", "we", "our") collects,
                uses, stores, and protects your information. We are committed to protecting your privacy and
                ensuring transparency in our data practices in accordance with the General Data Protection
                Regulation (GDPR) and applicable European data protection laws.
              </p>
            </section>

            {/* Key Principles */}
            <section className="pb-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-purple-700 mb-4">Our Privacy Principles</h2>
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600 font-bold">✓</span>
                    <span><strong>Privacy by Design:</strong> All data processing occurs locally on your device</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600 font-bold">✓</span>
                    <span><strong>Data Minimization:</strong> We only collect data essential for app functionality</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600 font-bold">✓</span>
                    <span><strong>No Third-Party Tracking:</strong> No analytics, advertising, or tracking SDKs</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600 font-bold">✓</span>
                    <span><strong>Full User Control:</strong> You can delete all your data at any time</span>
                  </li>
                </ul>
              </div>
            </section>

            {/* Data We Collect */}
            <section className="pb-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-purple-700 mb-4">1. Information We Collect</h2>

              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-gray-800 mb-2">1.1 Health and Fitness Data</h3>
                  <p className="mb-2">We collect and store <strong>locally on your device only</strong>:</p>
                  <ul className="list-disc list-inside ml-4 space-y-1">
                    <li><strong>Tinnitus Frequency Measurements:</strong> The frequency results (20-20,000 Hz) from your assessments</li>
                    <li><strong>Assessment Session Data:</strong> Session timestamps, assessment duration, and your responses during frequency detection</li>
                  </ul>
                  <p className="mt-2 text-sm text-gray-600">
                    <strong>Purpose:</strong> To provide the core functionality of detecting and tracking your tinnitus frequency over time.<br />
                    <strong>Legal Basis (GDPR):</strong> Consent and performance of contract (providing the app service you requested).
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-800 mb-2">1.2 Financial Information</h3>
                  <p className="mb-2">Subscription and purchase data processed by Google Play:</p>
                  <ul className="list-disc list-inside ml-4 space-y-1">
                    <li><strong>Subscription Status:</strong> Whether you have an active premium subscription (FREE/PREMIUM/EXPIRED)</li>
                    <li><strong>Purchase Timestamps:</strong> When subscriptions were purchased or expire</li>
                    <li><strong>Purchase Tokens:</strong> Google Play transaction identifiers (managed by Google)</li>
                  </ul>
                  <p className="mt-2 text-sm text-gray-600">
                    <strong>Note:</strong> We do not have access to your payment card details, billing address, or financial account information.
                    All payment processing is handled securely by Google Play.<br />
                    <strong>Purpose:</strong> Account management and premium feature access.<br />
                    <strong>Legal Basis (GDPR):</strong> Performance of contract and legitimate interests (managing subscriptions).
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-800 mb-2">1.3 App Activity and Settings</h3>
                  <p className="mb-2">Stored locally to enhance your experience:</p>
                  <ul className="list-disc list-inside ml-4 space-y-1">
                    <li><strong>App Preferences:</strong> Your chosen therapy settings, audio preferences, theme selection (dark/light mode)</li>
                    <li><strong>Session Information:</strong> Last detected frequency, last session date, onboarding completion status</li>
                    <li><strong>App Settings:</strong> Volume levels, haptic feedback preferences, notification preferences</li>
                  </ul>
                  <p className="mt-2 text-sm text-gray-600">
                    <strong>Purpose:</strong> To remember your preferences and provide a personalized experience.<br />
                    <strong>Legal Basis (GDPR):</strong> Legitimate interests (improving user experience).
                  </p>
                </div>
              </div>
            </section>

            {/* What We DON'T Collect */}
            <section className="pb-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-purple-700 mb-4">2. Information We Do NOT Collect</h2>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="font-semibold mb-2">We explicitly do NOT collect:</p>
                <div className="grid md:grid-cols-2 gap-2">
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li>Location data (GPS or approximate)</li>
                    <li>Personal identifiable information (name, email, address, phone number)</li>
                    <li>Photos or camera access</li>
                    <li>Microphone or audio recordings</li>
                    <li>Contacts list</li>
                    <li>Calendar data</li>
                  </ul>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li>Files and documents</li>
                    <li>SMS or messages</li>
                    <li>Device identifiers (IMEI, MAC address, advertising ID)</li>
                    <li>Web browsing history</li>
                    <li>Installed apps list</li>
                    <li>Analytics or usage tracking</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* How We Use Data */}
            <section className="pb-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-purple-700 mb-4">3. How We Use Your Information</h2>
              <p className="mb-3">We use the collected information solely for:</p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li><strong>App Functionality:</strong> Detecting and tracking your tinnitus frequency, providing personalized therapy audio</li>
                <li><strong>Subscription Management:</strong> Managing your premium subscription status and feature access</li>
                <li><strong>App Improvements:</strong> Understanding which features you use to improve the app (data stays on your device)</li>
                <li><strong>User Preferences:</strong> Remembering your settings and preferences for a better experience</li>
              </ul>
              <p className="mt-3 text-sm text-gray-600">
                <strong>Important:</strong> All processing happens locally on your device. We do not transmit your health data,
                frequency measurements, or personal information to external servers.
              </p>
            </section>

            {/* Data Storage and Security */}
            <section className="pb-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-purple-700 mb-4">4. Data Storage and Security</h2>

              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-gray-800 mb-2">4.1 Local Storage</h3>
                  <p>
                    All your health data and app preferences are stored <strong>exclusively on your Android device</strong> using:
                  </p>
                  <ul className="list-disc list-inside ml-4 space-y-1 mt-2">
                    <li><strong>Android DataStore:</strong> Encrypted local storage for preferences</li>
                    <li><strong>Room Database:</strong> Local SQLite database for assessment history</li>
                  </ul>
                  <p className="mt-2 text-sm text-gray-600">
                    This data is protected by Android's security mechanisms and your device's security (PIN, biometric authentication).
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-800 mb-2">4.2 Data Transmission</h3>
                  <p>
                    The app requires internet permissions for:
                  </p>
                  <ul className="list-disc list-inside ml-4 space-y-1 mt-2">
                    <li><strong>Google Play Billing:</strong> Secure communication with Google Play for subscription management (encrypted via HTTPS)</li>
                  </ul>
                  <p className="mt-2 text-sm text-gray-600">
                    <strong>No other data is transmitted.</strong> Your tinnitus frequency measurements and health data never leave your device.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-800 mb-2">4.3 Data Retention</h3>
                  <p>
                    Your data is retained on your device until:
                  </p>
                  <ul className="list-disc list-inside ml-4 space-y-1 mt-2">
                    <li>You manually delete it using the "Reset to Defaults" option in app settings</li>
                    <li>You clear the app's data through Android system settings</li>
                    <li>You uninstall the application</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Third-Party Services */}
            <section className="pb-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-purple-700 mb-4">5. Third-Party Services</h2>

              <div className="space-y-3">
                <div>
                  <h3 className="font-semibold text-gray-800 mb-2">5.1 Google Play Billing</h3>
                  <p className="mb-2">
                    We use Google Play's In-App Billing service for subscription management. Google Play processes:
                  </p>
                  <ul className="list-disc list-inside ml-4 space-y-1">
                    <li>Payment information (Google collects this, not us)</li>
                    <li>Purchase history and subscription status</li>
                    <li>Google Play account information</li>
                  </ul>
                  <p className="mt-2 text-sm text-gray-600">
                    Google Play's privacy practices are governed by the{' '}
                    <a
                      href="https://policies.google.com/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-purple-600 underline hover:text-purple-800"
                    >
                      Google Privacy Policy
                    </a>.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-800 mb-2">5.2 No Analytics or Tracking</h3>
                  <p>
                    We do <strong>NOT</strong> use:
                  </p>
                  <ul className="list-disc list-inside ml-4 space-y-1 mt-2">
                    <li>Google Analytics, Firebase Analytics, or any analytics platforms</li>
                    <li>Advertising networks or ad tracking</li>
                    <li>Crash reporting services (Crashlytics, Sentry, etc.)</li>
                    <li>Social media SDKs (Facebook, Twitter, etc.)</li>
                    <li>Any other third-party tracking or data collection services</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Your Rights (GDPR) */}
            <section className="pb-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-purple-700 mb-4">6. Your Rights Under GDPR</h2>
              <p className="mb-3">
                As a user in the European Economic Area (EEA), you have the following rights:
              </p>

              <div className="space-y-3">
                <div className="bg-gray-50 p-3 rounded">
                  <p className="font-semibold mb-1">🔍 Right to Access</p>
                  <p className="text-sm">
                    You can access all your data through the app's interface. All data is stored on your device.
                  </p>
                </div>

                <div className="bg-gray-50 p-3 rounded">
                  <p className="font-semibold mb-1">✏️ Right to Rectification</p>
                  <p className="text-sm">
                    You can modify your preferences and settings at any time through the app's settings menu.
                  </p>
                </div>

                <div className="bg-gray-50 p-3 rounded">
                  <p className="font-semibold mb-1">🗑️ Right to Erasure ("Right to be Forgotten")</p>
                  <p className="text-sm">
                    Delete all your data using "Reset to Defaults" in settings, or uninstall the app.
                  </p>
                </div>

                <div className="bg-gray-50 p-3 rounded">
                  <p className="font-semibold mb-1">📦 Right to Data Portability</p>
                  <p className="text-sm">
                    Your data is stored in standard Android formats. You can back up your data through Android's backup system.
                  </p>
                </div>

                <div className="bg-gray-50 p-3 rounded">
                  <p className="font-semibold mb-1">⛔ Right to Object</p>
                  <p className="text-sm">
                    You can stop data processing at any time by closing the app or adjusting permissions in Android settings.
                  </p>
                </div>

                <div className="bg-gray-50 p-3 rounded">
                  <p className="font-semibold mb-1">⚖️ Right to Lodge a Complaint</p>
                  <p className="text-sm">
                    You have the right to lodge a complaint with your national data protection authority.
                  </p>
                </div>
              </div>

              <p className="mt-4 text-sm text-gray-600">
                To exercise these rights or if you have questions, contact us at: tinnitustune@soepel.it
              </p>
            </section>

            {/* Children's Privacy */}
            <section className="pb-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-purple-700 mb-4">7. Children's Privacy</h2>
              <p>
                TinnitusTune is not intended for children under the age of 13 (or the applicable age of digital consent
                in your jurisdiction). We do not knowingly collect personal information from children. If you believe
                a child has provided us with personal information, please contact us immediately, and we will take
                steps to delete such information.
              </p>
            </section>

            {/* Medical Disclaimer */}
            <section className="pb-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-purple-700 mb-4">8. Medical Device Classification</h2>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="font-semibold text-amber-900 mb-2">⚠️ Important Notice</p>
                <p className="text-sm text-amber-800">
                  TinnitusTune is a <strong>wellness and sound therapy application</strong> for personal exploration.
                  It is <strong>NOT</strong> a medical device and has not been evaluated as a medical device under
                  the EU Medical Device Regulation (MDR 2017/745). This app is not intended to diagnose, treat, cure,
                  or prevent any disease or medical condition.
                </p>
                <p className="text-sm text-amber-800 mt-2">
                  <strong>Always consult qualified healthcare professionals or audiologists for medical advice regarding tinnitus.</strong>
                </p>
              </div>
            </section>

            {/* International Data Transfers */}
            <section className="pb-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-purple-700 mb-4">9. International Data Transfers</h2>
              <p className="mb-3">
                Since all data processing occurs locally on your device:
              </p>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>Your health data does not leave your device or country</li>
                <li>No cross-border data transfers occur</li>
                <li>Google Play Billing may involve data transfers, subject to Google's privacy policies and standard contractual clauses</li>
              </ul>
            </section>

            {/* Changes to Privacy Policy */}
            <section className="pb-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-purple-700 mb-4">10. Changes to This Privacy Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. When we make significant changes, we will notify
                you through the app or update the "Effective Date" at the top of this policy. We encourage you to
                review this Privacy Policy periodically for any changes.
              </p>
            </section>

            {/* Contact Information */}
            <section className="pb-6">
              <h2 className="text-xl font-semibold text-purple-700 mb-4">11. Contact Us</h2>
              <p className="mb-3">
                If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-semibold mb-2">Data Controller:</p>
                <p>Soepel IT</p>
                <p>Enschede, Netherlands</p>
                <p className="mt-2">
                  <strong>Email:</strong>{' '}
                  <a href="mailto:tinnitustune@soepel.it" className="text-purple-600 underline hover:text-purple-800">
                    tinnitustune@soepel.it
                  </a>
                </p>
              </div>
            </section>

            {/* Last Updated */}
            <section className="text-sm text-gray-500 pt-4 border-t border-gray-200">
              <p>
                <strong>Last Updated:</strong> {new Date().toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })}
              </p>
              <p className="mt-2">
                <strong>Version:</strong> 1.0
              </p>
            </section>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AndroidPrivacyPage;
